'use client';

import { CheckmarkCircleIcon } from '@app/app/_icons/CheckmarkCircleIcon';
import { CloseCircleIcon } from '@app/app/_icons/CloseCircleIcon';
import { CloseIcon } from '@app/app/_icons/CloseIcon';
import { ExclamationCircleIcon } from '@app/app/_icons/ExclamationCircleIcon';
import { InfoCircleIcon } from '@app/app/_icons/InfoCircleIcon';
import { useCallback } from 'react';
import { twMerge } from 'tailwind-merge';
import { ToastMessage } from './ToastContext';

interface Props {
    toast: ToastMessage;
    onClose: (id: string) => void;
}

export function Toast({ toast, onClose }: Props) {
    const handleClose = useCallback(() => {
        onClose(toast.id);
    }, [onClose, toast.id]);

    return (
        <div
            role="alert"
            className={twMerge(
                'flex min-w-80 items-center gap-3 rounded-lg px-4 py-3 shadow-lg duration-300 animate-in slide-in-from-right-full',
                toast.variant === 'danger' && 'border border-red-400 bg-red-100 font-medium',
                toast.variant === 'warning' && 'border border-orange-300 bg-orange-100 font-medium',
                toast.variant === 'info' && 'border border-primary-700 bg-white font-medium',
                toast.variant === 'success' && 'border border-primary-350 bg-primary-100 font-medium',
            )}>
            {toast.variant === 'danger' && <CloseCircleIcon className="size-6 shrink-0 text-red-500" />}
            {toast.variant === 'warning' && <ExclamationCircleIcon className="size-6 shrink-0 text-orange-500" />}
            {toast.variant === 'info' && <InfoCircleIcon className="size-6 shrink-0 text-primary-700" />}
            {toast.variant === 'success' && <CheckmarkCircleIcon className="size-6 shrink-0 text-primary-400" />}

            <div className="grow text-sm">{toast.message}</div>

            <button className="shrink-0 p-1" type="button" onClick={handleClose}>
                <CloseIcon className="size-4" />
            </button>
        </div>
    );
}
